/**
 * AdminFiscalAgentReleases — wydania usługi fiskalnej (panel ITE).
 *
 * Tu ITE wgrywa instalator, który klienci pobierają na swoje stanowiska z drukarką.
 *
 * DWIE RZECZY, KTÓRE TEN EKRAN CELOWO ROZDZIELA:
 *
 *  1. **Wgranie a udostępnienie.** Nowe wydanie jest niewidoczne dla klientów, dopóki ktoś go
 *     świadomie nie opublikuje. To plik wykonywalny uruchamiany na cudzym komputerze — zasługuje
 *     na moment, w którym można go pobrać i sprawdzić, zanim zrobi to ktokolwiek inny.
 *
 *  2. **Wycofanie a skasowanie.** Cofnięcie publikacji zdejmuje wydanie z panelu klienta, ale
 *     zostawia je tutaj: ktoś mógł je już zainstalować i musimy wiedzieć co. Kasowanie jest
 *     miękkie z tego samego powodu.
 *
 * Suma kontrolna jest pokazana, bo to jedyny sposób, żeby ktoś po drugiej stronie potwierdził,
 * że pobrał dokładnie ten plik, który wydaliśmy.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface AgentRelease {
  id: string;
  platform: 'windows' | 'macos';
  version: string;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  notes: string | null;
  isPublished: boolean;
  publishedAt: string | null;
  createdAt: string;
}

const PLATFORM_LABEL: Record<AgentRelease['platform'], string> = {
  windows: 'Windows',
  macos: 'macOS',
};

function formatSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function AdminFiscalAgentReleases() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toDelete, setToDelete] = useState<AgentRelease | null>(null);

  const releasesQuery = useQuery({
    queryKey: ['admin', 'fiscal-agent-releases'],
    queryFn: async () => {
      const res = await api.get<{ items: AgentRelease[] }>('/admin/fiscal-agent-releases');
      return res.data.items;
    },
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'fiscal-agent-releases'] });

  const publishMutation = useMutation({
    mutationFn: (input: { id: string; isPublished: boolean }) =>
      api.patch(`/admin/fiscal-agent-releases/${input.id}`, { isPublished: input.isPublished }),
    onSuccess: () => {
      void refresh();
    },
    onError: () => toast.show({ variant: 'error', title: 'Nie udało się zmienić publikacji' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/fiscal-agent-releases/${id}`),
    onSuccess: () => {
      setToDelete(null);
      void refresh();
      toast.show({ variant: 'success', title: 'Wydanie usunięte' });
    },
    onError: () => toast.show({ variant: 'error', title: 'Nie udało się usunąć wydania' }),
  });

  const releases = releasesQuery.data ?? [];

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-text-primary">Usługa fiskalna — wydania</h1>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setUploadOpen(true)}
          data-testid="agent-release-new"
        >
          Wgraj instalator
        </Button>
      </div>

      <p className="mb-3 text-xs text-text-secondary">
        Instalator pobierają klienci w ustawieniach „Drukarki fiskalne". Wgranie nie udostępnia
        pliku — trzeba go jeszcze opublikować.
      </p>

      {releasesQuery.isLoading ? (
        <p className="text-sm text-text-secondary">Ładowanie…</p>
      ) : releases.length === 0 ? (
        <p className="text-sm text-text-secondary" data-testid="agent-releases-empty">
          Nie wgrano jeszcze żadnego instalatora.
        </p>
      ) : (
        <div className="space-y-2">
          {releases.map((release) => (
            <div
              key={release.id}
              className="flex items-start justify-between gap-3 rounded border border-border-primary px-3 py-2"
              data-testid={`agent-release-${release.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                  {PLATFORM_LABEL[release.platform]} · {release.version}
                  <span
                    className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                      release.isPublished
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-bg-tertiary text-text-secondary'
                    }`}
                  >
                    {release.isPublished ? 'Opublikowane' : 'Nieopublikowane'}
                  </span>
                </div>
                <div className="mt-0.5 truncate text-xs text-text-secondary">
                  {release.fileName} · {formatSize(release.sizeBytes)}
                </div>
                {release.notes && (
                  <div className="mt-0.5 text-xs text-text-secondary">{release.notes}</div>
                )}
                <div className="mt-0.5 break-all font-mono text-[11px] text-text-tertiary">
                  SHA-256: {release.sha256}
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={publishMutation.isPending}
                  onClick={() =>
                    publishMutation.mutate({ id: release.id, isPublished: !release.isPublished })
                  }
                  data-testid={`agent-release-toggle-${release.id}`}
                >
                  {release.isPublished ? 'Wycofaj' : 'Opublikuj'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setToDelete(release)}>
                  Usuń
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {uploadOpen && (
        <UploadModal
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            void refresh();
            toast.show({
              variant: 'success',
              title: 'Instalator wgrany — opublikuj go, żeby klienci go zobaczyli',
            });
          }}
        />
      )}

      <ConfirmDialog
        open={!!toDelete}
        variant="danger"
        title="Usunąć wydanie?"
        message={
          toDelete
            ? `Wydanie ${PLATFORM_LABEL[toDelete.platform]} ${toDelete.version} zniknie z panelu klientów. Ci, którzy już je zainstalowali, zachowają działającą usługę.`
            : ''
        }
        onConfirm={() => toDelete && deleteMutation.mutate(toDelete.id)}
        onClose={() => setToDelete(null)}
      />
    </div>
  );
}

function UploadModal({ onClose, onUploaded }: { onClose: () => void; onUploaded: () => void }) {
  const toast = useToast();
  const [platform, setPlatform] = useState<'windows' | 'macos'>('windows');
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Komunikaty z serwera na język, który coś mówi osobie wgrywającej plik. */
  const ERROR_TEXT: Record<string, string> = {
    INVALID_VERSION: 'Wersja może zawierać tylko litery, cyfry, kropki i myślniki.',
    FILE_REQUIRED: 'Wskaż plik instalatora.',
    FILE_TOO_LARGE: 'Plik przekracza dopuszczalny rozmiar (200 MB).',
    EXTENSION_NOT_ALLOWED:
      'To nie jest instalator dla wybranego systemu (Windows: exe, msi, zip; macOS: pkg, dmg, zip).',
    VERSION_ALREADY_EXISTS: 'Ta wersja dla tego systemu już istnieje.',
    STORAGE_UPLOAD_FAILED: 'Nie udało się zapisać pliku w magazynie.',
  };

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.append('platform', platform);
      form.append('version', version.trim());
      if (notes.trim()) form.append('notes', notes.trim());
      form.append('file', file!);
      await api.post('/admin/fiscal-agent-releases', form);
    },
    onSuccess: onUploaded,
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(ERROR_TEXT[code ?? ''] ?? 'Nie udało się wgrać instalatora.');
      toast.show({ variant: 'error', title: 'Wgrywanie nie powiodło się' });
    },
  });

  return (
    <Modal open onClose={onClose} title="Wgraj instalator usługi fiskalnej">
      <div className="space-y-3">
        {error && (
          <p
            className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
            role="alert"
          >
            {error}
          </p>
        )}

        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary" htmlFor="ar-platform">
            System
          </label>
          <Select
            id="ar-platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as 'windows' | 'macos')}
            options={[
              { value: 'windows', label: 'Windows' },
              { value: 'macos', label: 'macOS' },
            ]}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary" htmlFor="ar-version">
            Wersja
          </label>
          <Input
            id="ar-version"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="1.0.0"
            data-testid="agent-release-version"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary" htmlFor="ar-notes">
            Co się zmieniło (opcjonalnie)
          </label>
          <Input
            id="ar-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Obsługa numeru dokumentu w stopce paragonu"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-text-primary" htmlFor="ar-file">
            Plik instalatora
          </label>
          {/* Wybór pliku nie ma odpowiednika w bibliotece prymitywów — świadomy surowy input. */}
          {/* eslint-disable-next-line no-restricted-syntax */}
          <input
            id="ar-file"
            type="file"
            className="block w-full text-sm text-text-secondary"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            data-testid="agent-release-file"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            disabled={!file || !version.trim() || uploadMutation.isPending}
            onClick={() => {
              setError(null);
              uploadMutation.mutate();
            }}
            data-testid="agent-release-submit"
          >
            {uploadMutation.isPending ? 'Wgrywam…' : 'Wgraj'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
